import React, { useMemo, useRef, useState } from "react";
import InputBox from "../../components/InputBox";
import AddProducts from "../setting/Products/AddProducts";
import { ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";
import Image from "src/shared/Image";
import { CategoryV2, Product } from "src/shared/services/interfacev2";
import usePageSize from "../../hooks/pageSize";
import imageOrther from "src/assets/Images/images.png";
import iconMap from "src/assets/icons/Icons";

type Props = {
  product: Product[];
  category: CategoryV2[];
  sendToOrder: (productId: string) => void;
  clearData: string;
  productLoading: (data: boolean) => void;
  syncProduct: () => void;
};

export const enableStock = false;

const ProductDisplay: React.FC<Props> = React.memo(({ product, category, sendToOrder, clearData, productLoading, syncProduct }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [searchCategory, setSearchCategory] = useState<string>("All Category");
  const [addProduct, setAddProduct] = useState<boolean>(false);
  const colorSet = ThemeBasedColorHeader();
  const view = usePageSize();
  const [isOpen, setIsOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { Sync } = iconMap;

  /** 🔍 Filter categories by name */
  const filteredCategories = useMemo(() => {
    const search = searchCategory.trim().toLowerCase();
    return search ? category.filter((c) => c.name.toLowerCase().includes(search)) : category;
  }, [searchCategory, category]);

  /** 🧮 Filter products by category and search */
  const filteredProducts = useMemo(() => {
    if (!product) return [];
    let result = [...product];

    if (selectedCategory) {
      const categoryObj = category.find((c) => c.name.toLowerCase() === selectedCategory.toLowerCase());
      if (categoryObj) {
        result = result.filter((p) => p.categoryId === categoryObj._id.$oid);
      }
    }

    if (searchText.trim()) {
      result = result.filter((p) => p.productName.toLowerCase().includes(searchText.toLowerCase()));
    }

    return result;
  }, [selectedCategory, searchText, product, category]);

  /** 🧾 Product title tooltip */
  const productTitle = (p: Product) => `${p.productName} (Stock: ${p.unitConfiguration.stock || 0})`;
  const handleSelectCategory = (category: CategoryV2) => {
    setSelectedCategory(category.name);
    setSearchCategory(category.name);
    setIsOpen(false);
  };
  return (
    <div className='flex flex-col w-full p-4 bg-gray-50 h-full border rounded-lg overflow-hidden shadow-md'>
      {/* Header */}
      <div className='w-full flex justify-between items-center'>
        <div className='flex items-center gap-2'>
          <h1 className='text-xl font-bold text-gray-700'>🛍️ Product </h1>
          <button
            onClick={() => {
              setIsSyncing(true);
              syncProduct();
              setTimeout(() => setIsSyncing(false), 2000);
            }}
            className={`py-1 rounded-full hover:bg-gray-200 transition-colors ${isSyncing ? "animate-spin" : ""}`}
            style={{ animationDirection: "reverse" }}
            title="Sync Products"
          >
            {Sync()}
          </button>
        </div>
        <div className='flex gap-2 w-1/2 justify-end'>
          {view !== "pos" && (
            <div className='relative flex justify-center items-center gap-2' ref={dropdownRef}>
              {/* Search Input */}
              <div>Categoty </div>
              <div className='relative w-48'>
                <InputBox
                  ref={inputRef}
                  placeholder='Search Categories'
                  value={searchCategory}
                  onClick={() => setIsOpen(true)}
                  onChange={(e) => {
                    setSearchCategory(e.target.value);
                    setIsOpen(true);
                  }}
                  className='pr-8'
                />

                {/* Clear Button */}
                {searchCategory && (
                  <button
                    type='button'
                    onClick={() => {
                      setSearchCategory("");
                      setIsOpen(true);
                      inputRef.current?.focus(); // focus input
                    }}
                    className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm'
                  >
                    ✕
                  </button>
                )}
              </div>
              {/* Dropdown */}
              {isOpen && filteredCategories.length > 0 && (
                <div className='absolute w-48 z-10 bg-white border border-gray-300 mt-1 rounded-md shadow-lg max-h-40 overflow-y-auto top-8 ml-20'>
                  <div
                    onClick={() => {
                      setSelectedCategory("");
                      setSearchCategory("All Category");
                      setIsOpen(false);
                    }}
                    className='p-2 cursor-pointer hover:bg-blue-100'
                  >
                    <p className='font-semibold'>All Category</p>
                  </div>
                  {filteredCategories.map((cust: CategoryV2, index: number) => (
                    <div key={index} onClick={() => handleSelectCategory(cust)} className='p-2 cursor-pointer hover:bg-blue-100'>
                      <p className='font-semibold'>{cust.name}</p>
                      <p className='text-sm text-gray-500'>{cust.secondName}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {view === "pos" && (
            <InputBox
              placeholder='Search Products'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className='border border-gray-400 focus:ring-2 focus:ring-blue-500'
              aria-label='Search Products'
            />
          )}
        </div>
      </div>

      <div className={`flex flex-1 h-[90%] gap-2 ${view === "pos" ? "overflow-hidden" : "flex-col "}`}>
        {/* Category Sidebar */}
        <aside className={`flex flex-col ${view === "pos" ? "w-1/4 h-full" : "w-full "} `}>
          {view === "pos" ? (
            <div className='h-[94%] overflow-y-auto mt-2 space-y-1 bg-white rounded p-2 shadow-inner'>
              {/* All Categories */}
              <div
                className={`px-2 py-2 rounded text-center font-medium transition-all ${selectedCategory === "" ? `${colorSet} text-white cursor-default` : "bg-gray-200 hover:bg-gray-300 cursor-pointer"
                  }`}
                onClick={() => setSelectedCategory("")}
              >
                All Categories
              </div>
              {/* Filtered Categories */}
              {category.length > 0 ? (
                category.map((cat) => (
                  <div
                    key={cat._id.$oid}
                    className={`px-2 py-2 rounded text-center font-medium transition-all ${selectedCategory.toLowerCase() === cat.name.toLowerCase()
                      ? `${colorSet} text-white cursor-default`
                      : "bg-gray-200 hover:bg-gray-300 cursor-pointer"
                      }`}
                    onClick={() => setSelectedCategory(cat.name)}
                  >
                    {cat.name}
                  </div>
                ))
              ) : (
                <p className='text-center text-sm text-gray-500 py-2'>No categories found</p>
              )}
            </div>
          ) : (
            ""
          )}
        </aside>

        {/* Product Grid */}
        <section className={"w-full flex flex-col " + (view === "pos" ? " h-full" : " h-[96%]")}>
          {view !== "pos" && (
            <InputBox
              placeholder='Search Products'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className='border border-gray-400 focus:ring-2 focus:ring-blue-500'
              aria-label='Search Products'
            />
          )}

          <div className='grid gap-4 mt-1 overflow-y-auto overflow-x-hidden max-h-[calc(100vh-350px)] sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-3'>
            {filteredProducts.length > 0 ? (
              <>
                {filteredProducts.map((p) => (
                  <button
                    key={p._id?.$oid}
                    onClick={() => sendToOrder(p._id?.$oid)}
                    disabled={p.unitConfiguration.stock <= 0 && enableStock}
                    title={productTitle(p)}
                    className={`relative p-3 border rounded-lg shadow-md transition-all duration-200 group 
                    ${p.unitConfiguration.stock <= 0 && enableStock ? "bg-red-300 cursor-not-allowed opacity-70" : "bg-white hover:shadow-lg hover:border-blue-400"}`}
                  >
                    <div className='flex flex-col items-center'>
                      <Image imagePath={p.imageUrl} className='w-[100px] h-[100px] object-contain mb-2 rounded-md' />
                      <div className='flex flex-col items-center gap-1 w-full'>
                        <p className='text-sm font-semibold text-gray-700 text-center truncate w-full'>{p.productName}</p>
                        <p className='text-xs text-gray-500 truncate w-full text-center'>{p.arabicName}</p>
                        <p className='text-sm font-bold text-green-700 mt-1'>{Number(p.amount).toFixed(2)} QAR</p>
                      </div>
                    </div>

                    {/* highlight shimmer */}
                    <span className='absolute inset-0 rounded-lg opacity-0 group-hover:opacity-10 bg-blue-500 transition-opacity duration-300'></span>
                  </button>
                ))}
                {/* ✅ Single "Other" Button */}
                <button
                  onClick={() => sendToOrder("other")}
                  className='relative p-3 border rounded-lg shadow-md transition-all duration-200 group 
                   bg-white hover:shadow-lg hover:border-blue-400'
                >
                  <div className='flex flex-col items-center'>
                    <Image imagePath={imageOrther} className='w-[100px] h-[100px] object-contain mb-2 rounded-md' />
                    <div className='flex flex-col items-center gap-1 w-full'>
                      <p className='text-sm font-semibold text-gray-700 text-center truncate w-full'>Other</p>
                      <p className='text-xs text-gray-500 truncate w-full text-center'>آحرون</p>
                      <p className='text-sm font-bold text-green-700 mt-1'>0.00 QAR</p>
                    </div>
                  </div>
                </button>
              </>
            ) : (
              <button
                onClick={() => sendToOrder("other")}
                className='relative p-3 border rounded-lg shadow-md transition-all duration-200 group 
                   bg-white hover:shadow-lg hover:border-blue-400'
              >
                <div className='flex flex-col items-center'>
                  <Image imagePath={imageOrther} className='w-[100px] h-[100px] object-contain mb-2 rounded-md' />
                  <div className='flex flex-col items-center gap-1 w-full'>
                    <p className='text-sm font-semibold text-gray-700 text-center truncate w-full'>Other</p>
                    <p className='text-xs text-gray-500 truncate w-full text-center'>آحرون</p>
                    <p className='text-sm font-bold text-green-700 mt-1'>0.00 QAR</p>
                  </div>
                </div>
              </button>
            )}
          </div>
        </section>
      </div>

      {/* Add Product Modal */}
      {addProduct && (
        <AddProducts close={() => setAddProduct(false)} category={category} loading={productLoading} addmul={true} addProduct={addProduct} />
      )}
    </div>
  );
});

export default ProductDisplay;
